import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Divider, 
  Chip, 
  CircularProgress, 
  Alert, 
  Button, 
  Tooltip, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  useTheme,
  alpha,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  lighten,
  darken
} from '@mui/material';
import { formatPercent, formatCurrency } from '../utils/formatters';
import { StockChart } from './charts/StockChart';
import { useProfile } from '../contexts/ProfileContext';
import { useLoadingStatus } from '../contexts/LoadingStatusContext';
import { investmentAPI } from '../api/investmentAPI';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import SpeedIcon from '@mui/icons-material/Speed';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import { getSignalColor, getTrendColor, semanticColors } from '../theme/themesV3';

// Helper function to map rating strings to standardized format for color lookup
const mapRatingToSignal = (rating: string | undefined): string => {
  if (!rating) return 'Hold';
  const normalized = rating.toLowerCase().trim();
  
  if (normalized.includes('strong') && normalized.includes('buy')) return 'Strong Buy';
  if (normalized === 'buy' || (normalized.includes('buy') && !normalized.includes('strong'))) return 'Buy';
  if (normalized.includes('hold') || normalized.includes('neutral')) return 'Hold';
  if (normalized === 'sell' || (normalized.includes('sell') && !normalized.includes('strong'))) return 'Sell';
  if (normalized.includes('strong') && normalized.includes('sell')) return 'Strong Sell';
  
  return 'Hold'; // Default
};

// Helper component to show both base and adjusted values with improved styling
const CompareValues = ({ 
  baseValue, 
  adjustedValue, 
  formatter = (val: any) => val, 
  baseColor = "text.secondary", 
  adjustedColor = "text.primary",
  isPositive
}: {
  baseValue: any;
  adjustedValue: any;
  formatter?: (val: any) => any;
  baseColor?: string;
  adjustedColor?: string;
  isPositive?: boolean;
}) => {
  const theme = useTheme();
  
  if (baseValue === adjustedValue || baseValue === undefined || adjustedValue === undefined) {
    // Use getTrendColor from themesV3 if it's a trend-based value
    const displayColor = isPositive !== undefined 
      ? getTrendColor(isPositive ? 1 : -1, theme)
      : adjustedColor;
      
    return (
      <Typography 
        variant="body1" 
        color={displayColor} 
        sx={{ 
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {isPositive !== undefined && (
          isPositive 
            ? <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} /> 
            : <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
        )}
        {formatter(adjustedValue) ?? 'N/A'}
      </Typography>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Typography 
        variant="body1" 
        color={
          isPositive !== undefined 
            ? getTrendColor(isPositive ? 1 : -1, theme)
            : adjustedColor
        } 
        sx={{ 
          lineHeight: 1.2, 
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        {isPositive !== undefined && (
          isPositive 
            ? <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} /> 
            : <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
        )}
        {formatter(adjustedValue)}
      </Typography>
      <Typography 
        variant="caption" 
        color={baseColor} 
        sx={{ textDecoration: 'line-through', opacity: 0.7 }}
      >
        {formatter(baseValue)}
      </Typography>
    </Box>
  );
};

// For range display in a more compact way:
const CompareRange = ({ 
  baseLower, 
  baseUpper, 
  adjustedLower, 
  adjustedUpper, 
  formatter = (val: any) => val
}: {
  baseLower: any;
  baseUpper: any;
  adjustedLower: any;
  adjustedUpper: any;
  formatter?: (val: any) => any;
}) => {
  const theme = useTheme();
  const showBase = (baseLower !== adjustedLower || baseUpper !== adjustedUpper) && 
                  (baseLower !== undefined && baseUpper !== undefined);
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        borderRadius: 1,
        px: 1,
        py: 0.5
      }}>
        <TimelineIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.info.main }} />
        <Typography variant="body2">
          Range: {formatter(adjustedLower)} - {formatter(adjustedUpper)}
        </Typography>
      </Box>
      {showBase && (
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ textDecoration: 'line-through', opacity: 0.7, mt: 0.5, display: 'block' }}
        >
          Range: {formatter(baseLower)} - {formatter(baseUpper)}
        </Typography>
      )}
    </Box>
  );
};

// Helper component for section headers with icons
const SectionHeader = ({ title, icon, color = "primary.main" }: { title: string, icon?: React.ReactNode, color?: string }) => {
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2,
        pb: 1,
        borderBottom: `2px solid ${theme.palette.divider}`
      }}
    >
      {icon && (
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center', 
            mr: 1.5,
            color: color,
            backgroundColor: alpha(theme.palette.mode === 'dark' ? theme.palette.info.main : color, 0.1),
            borderRadius: '50%',
            width: 36,
            height: 36
          }}
        >
          {icon}
        </Box>
      )}
      <Typography variant="h6" color={color} fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );
};

// Helper component for metric cards
const MetricCard = ({ 
  label, 
  value, 
  icon, 
  trend = 0, 
  size = "medium",
  isAlert = false
}: { 
  label: string; 
  value: string | number; 
  icon?: React.ReactNode;
  trend?: number;
  size?: "small" | "medium" | "large";
  isAlert?: boolean;
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isPositive = trend > 0;
  const isNegative = trend < 0;
  
  // Use theme tokens instead of hardcoded colors
  const bgColor = alpha(theme.palette.info.main, 0.85);  // Use info.main which is correctly mapped in themesV3

  // Add a subtle inner shadow for depth
  const boxShadow = 'inset 0 0 5px rgba(0,0,0,0.1)';

  // Border color - reference theme color
  const borderColor = alpha(theme.palette.info.main, 0.95);

  // Use theme-aware text colors
  const labelColor = theme.palette.common.white;
  
  // Theme-aware trend colors
  const valueColor = isAlert 
    ? theme.palette.warning.main  // Use warning.main for alerts
    : isPositive 
      ? semanticColors.gain  // Use direct import instead of theme.palette.custom
      : isNegative 
        ? semanticColors.loss  // Use direct import instead of theme.palette.custom
        : theme.palette.common.white;  // Default white
  
  // Icon styling with theme-aware colors
  const iconBgColor = alpha(theme.palette.common.white, 0.15);
  const iconColor = theme.palette.common.white;
  
  return (
    <Box 
      sx={{ 
        p: size === "small" ? 1 : 1.5, 
        borderRadius: 2,
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: boxShadow,
      }}
    >
      {/* Header row with label and icon */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 1,
        pb: 1,
        borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}` // Subtle separator line
      }}>
        <Typography variant="body2" fontWeight={600} color={labelColor}>
          {label}
        </Typography>
        {icon && (
          <Box 
            sx={{
              backgroundColor: iconBgColor,
              borderRadius: '50%',
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: iconColor
            }}
          >
            {isPositive ? (
              <TrendingUpIcon fontSize="small" sx={{ color: semanticColors.gain }} />
            ) : (
              <TrendingDownIcon fontSize="small" sx={{ color: semanticColors.loss }} />
            )}
          </Box>
        )}
      </Box>
      
      {/* Value display */}
      <Typography 
        variant={size === "small" ? "body1" : "h6"} 
        fontWeight={700} // Bolder text for values to match screenshot
        sx={{ color: valueColor }}
      >
        {value}
      </Typography>
      
      {/* Trend indicator if applicable */}
      {trend !== 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          {isPositive ? (
            <TrendingUpIcon fontSize="small" sx={{ color: semanticColors.gain }} />
          ) : (
            <TrendingDownIcon fontSize="small" sx={{ color: semanticColors.loss }} />
          )}
          <Typography 
            variant="caption" 
            sx={{ 
              ml: 0.5,
              color: isPositive ? semanticColors.gain : semanticColors.loss
            }}
          >
            {Math.abs(trend).toFixed(2)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Styled tag/chip for ratings
const RatingTag = ({ rating }: { rating: string }) => {
  const theme = useTheme();
  const standardizedRating = mapRatingToSignal(rating);
  const ratingColor = getSignalColor(standardizedRating);
  
  const isPositive = rating?.toLowerCase().includes('buy');
  const isNegative = rating?.toLowerCase().includes('sell');

  return (
    <Box 
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 4,
        px: 1.5,
        py: 0.5,
        backgroundColor: ratingColor,
        color: theme.palette.getContrastText(ratingColor),
        fontWeight: 600
      }}
    >
      {isPositive && <ThumbUpIcon fontSize="small" sx={{ mr: 0.5 }} />}
      {isNegative && <ThumbDownIcon fontSize="small" sx={{ mr: 0.5 }} />}
      {rating || 'N/A'}
    </Box>
  );
};

// Helper function to determine color based on value
const getColorForValue = (value: number, theme: any) => {
  return getTrendColor(value, theme);
};

const AdditionalTickerDetails = ({ details }: { details: any }) => {
  const theme = useTheme();
  
  // Helper to render a key-value pair item with improved styling
  const renderDetailItem = (label: string, value: any, valueFormatter?: (val: any) => string, subLabel?: string) => {
    // Determine if value is a number/percentage and should be colored
    let isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
    let isPercentage = label.toLowerCase().includes('growth') || 
                      label.toLowerCase().includes('margin') || 
                      label.toLowerCase().includes('ratio') ||
                      label.toLowerCase().includes('yield');
    
    let valueColor = isNumeric && isPercentage
      ? parseFloat(value) > 0 
        ? getTrendColor(1, theme)
        : parseFloat(value) < 0 
          ? getTrendColor(-1, theme)
          : theme.palette.text.primary
      : theme.palette.text.primary;
    
    return (
      <Grid item xs={12} sm={6} md={4} key={label + (subLabel || '')}>
        <Box sx={{ 
          p: 1.5, 
          borderRadius: 2, 
          height: '100%',
          backgroundColor: alpha(theme.palette.background.paper, 0.7),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <Typography variant="caption" color="text.secondary" component="div" fontWeight={500}>
            {label}
            {subLabel && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                component="div" 
                sx={{fontSize: '0.7rem', fontStyle: 'italic'}}
              >
                {subLabel}
              </Typography>
            )}
          </Typography>
          <Typography 
            variant="body2" 
            fontWeight="medium" 
            sx={{ 
              mt: 0.5, 
              color: valueColor,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {isNumeric && isPercentage && parseFloat(value) > 0 && (
              <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
            )}
            {isNumeric && isPercentage && parseFloat(value) < 0 && (
              <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
            )}
            {valueFormatter ? valueFormatter(value) : (value ?? 'N/A')}
          </Typography>
        </Box>
      </Grid>
    );
  };

  // Updated trade action item with better visualization
  const renderTradeActionItem = (persona: string, action?: string, reason?: string) => {
    const actionLower = action?.toLowerCase() || '';
    const isBuy = actionLower.includes('buy');
    const isSell = actionLower.includes('sell');
    const isHold = actionLower.includes('hold');
    
    let actionColor = isBuy 
      ? getTrendColor(1, theme)
      : isSell 
        ? getTrendColor(-1, theme)
        : isHold 
          ? getTrendColor(0, theme)
          : theme.palette.text.primary;
          
    let bgColor = alpha(actionColor, 0.1);
    
    return (
      <Grid item xs={12} sm={6} key={persona}>
        <Box sx={{ 
          p: 2, 
          borderRadius: 2, 
          backgroundColor: bgColor,
          border: `1px solid ${alpha(actionColor, 0.2)}`,
          height: '100%'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Avatar 
              sx={{ 
                bgcolor: actionColor, 
                width: 32, 
                height: 32,
                mr: 1
              }}
            >
              {persona.charAt(0)}
            </Avatar>
            <Typography variant="subtitle2" fontWeight="600" color="text.primary">
              {persona}
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1,
            p: 1,
            borderRadius: 1,
            backgroundColor: alpha(actionColor, 0.15)
          }}>
            <Typography 
              variant="body2" 
              fontWeight="600" 
              sx={{ 
                color: actionColor,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {isBuy && <ThumbUpIcon fontSize="small" sx={{ mr: 0.5 }} />}
              {isSell && <ThumbDownIcon fontSize="small" sx={{ mr: 0.5 }} />}
              {action || 'N/A'}
            </Typography>
          </Box>
          
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
            Reason:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {reason || 'N/A'}
          </Typography>
        </Box>
      </Grid>
    );
  };

  let parsedNews: { date: string; article: string }[] = [];
  if (details.news) {
    try {
      const rawNews = JSON.parse(details.news);
      // Assuming the first row is headers, skip it
      if (Array.isArray(rawNews) && rawNews.length > 1) {
        parsedNews = rawNews.slice(1).map((item: any[]) => ({
          date: item[0],
          article: item[1],
        }));
      }
    } catch (e) {
      console.error("TickerDetailView: Error parsing news data:", e);
      // Keep parsedNews as empty array on error
    }
  }

  const formatNewsDate = (dateStr: string) => {
    if (!dateStr || typeof dateStr !== 'string' || dateStr.length < 14) return 'N/A';
    try {
      // Format: YYYYMMDDTHHMMSS -> YYYY-MM-DDTHH:MM:SSZ
      const formattedDateStr = `${dateStr.substring(0,4)}-${dateStr.substring(4,6)}-${dateStr.substring(6,8)}T${dateStr.substring(9,11)}:${dateStr.substring(11,13)}:${dateStr.substring(13,15)}Z`;
      return new Date(formattedDateStr).toLocaleString();
    } catch (e) {
      console.error("TickerDetailView: Error formatting news date:", e);
      return 'Invalid Date';
    }
  };
  
  const formatLastUpdated = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  return (
    <Paper 
      sx={{ 
        p: 2, 
        mt: 1, 
        backgroundColor: 'background.default',
        borderRadius: 3,
        boxShadow: theme.shadows[2]
      }}
    >
      <Grid container spacing={2}>
        {/* Summary Section */}
        {details.Summary && (
          <Grid item xs={12}>
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardHeader 
                title={
                  <SectionHeader 
                    title="Overall Summary" 
                    icon={<AssessmentIcon />} 
                    color={theme.palette.info.main}
                  />
                }
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                  {details.Summary}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Fundamental Highlights Section */}
        <Grid item xs={12}>
          <SectionHeader 
            title="Fundamental Highlights" 
            icon={<ShowChartIcon />} 
            color={theme.palette.primary.main}
          />
        </Grid>
        {renderDetailItem('Revenue', details.revenue, formatCurrency)}
        {renderDetailItem('Net Margin', details.net_margin, (val) => formatPercent(val / 100))}
        {renderDetailItem('EBITDA Margin', details.ebitda_margin, (val) => formatPercent(val / 100))}
        {renderDetailItem('Revenue YoY Growth', details.revenue_yoy, (val) => formatPercent(val / 100))}
        {renderDetailItem('EPS YoY Growth', details.eps_yoy, (val) => formatPercent(val / 100))}
        {renderDetailItem('Institutional Holding Ratio', details.institutional_hold_to_marketcap_ratio, formatPercent)}
        
        <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>

        {/* Trade Signals & Actions Section */}
        <Grid item xs={12}>
          <SectionHeader 
            title="Trade Signals & Actions" 
            icon={<PriceCheckIcon />} 
            color={theme.palette.secondary.main}
          />
        </Grid>
        
        {/* Buy/Sell Details */}
        <Grid item xs={12} container spacing={2}>
          {details['Buy_Details.Price'] && (
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  mb: 2, 
                  borderRadius: 2, 
                  height: '100%',
                  backgroundColor: alpha(theme.palette.success.main, 0.05),
                  borderLeft: `4px solid ${theme.palette.success.main}`
                }}
              >
                <CardHeader 
                  title="Buy Signal"
                  avatar={
                    <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                      <ThumbUpIcon />
                    </Avatar>
                  }
                  sx={{ pb: 0 }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Price Target
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        {formatCurrency(details['Buy_Details.Price'])}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Timeline
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {details['Buy_Details.TimelineInDays']} days
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Reason
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {details['Buy_Details.Reason'] ?? 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {details['Sell_Details.Price'] && (
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  mb: 2, 
                  borderRadius: 2, 
                  height: '100%',
                  backgroundColor: alpha(theme.palette.error.main, 0.05),
                  borderLeft: `4px solid ${theme.palette.error.main}`
                }}
              >
                <CardHeader 
                  title="Sell Signal (if holding)"
                  avatar={
                    <Avatar sx={{ bgcolor: theme.palette.error.main }}>
                      <ThumbDownIcon />
                    </Avatar>
                  }
                  sx={{ pb: 0 }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Price Target
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="error.main">
                        {formatCurrency(details['Sell_Details.Price'])}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Timeline
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {details['Sell_Details.TimelineInDays']} days
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Reason
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {details['Sell_Details.Reason'] ?? 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {details['Trade_Actions_By_Persona.Aggressive_Trader.Action'] && (
          <>
            <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>
            <Grid item xs={12}>
                 <SectionHeader 
                   title="Persona-Based Actions" 
                   icon={<LocalAtmIcon />} 
                   color={theme.palette.info.main}
                 />
            </Grid>
            {renderTradeActionItem(
              'Aggressive Trader',
              details['Trade_Actions_By_Persona.Aggressive_Trader.Action'],
              details['Trade_Actions_By_Persona.Aggressive_Trader.Reason']
            )}
            {renderTradeActionItem(
              'Conservative Trader',
              details['Trade_Actions_By_Persona.Conservative_Trader.Action'],
              details['Trade_Actions_By_Persona.Conservative_Trader.Reason']
            )}
          </>
        )}

        {/* News Section */}
        {parsedNews.length > 0 && (
          <>
            <Grid item xs={12}><Divider sx={{ my: 2 }} /></Grid>
            <Grid item xs={12}>
              <SectionHeader 
                title="Recent News (Top 5)" 
                icon={<InfoOutlinedIcon />} 
                color={theme.palette.info.main}
              />
              {parsedNews.slice(0, 5).map((newsItem, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    mb: 1.5, 
                    p: 1.5, 
                    borderRadius: 2, 
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    borderLeft: `3px solid ${theme.palette.info.main}`,
                    "&:hover": {
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: 1
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    display="block" 
                    fontWeight={500}
                    sx={{ mb: 0.5 }}
                  >
                    {formatNewsDate(newsItem.date)}
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {newsItem.article}
                  </Typography>
                </Box>
              ))}
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};


interface Props {
  tickerInfo?: any;
  ticker: string;
  onBack: () => void;
}

const TickerDetailView: React.FC<Props> = ({ tickerInfo, ticker, onBack }) => {
  const theme = useTheme();
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);
  const { currentProfile } = useProfile();
  const [expandedAccordion, setExpandedAccordion] = useState<boolean>(false);

  // State for local UI loading only (faster feedback)
  const [loading, setLoading] = useState<boolean>(!tickerInfo);
  const [tickerDetails, setTickerDetails] = useState<any>(tickerInfo);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tickerInfo) {
      setLoading(true);
      try {
        // Handle both array and object formats
        if (Array.isArray(tickerInfo)) {
          setTickerDetails(tickerInfo[0]);
        } else {
          setTickerDetails(tickerInfo);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error processing ticker info:", error);
        setError("Failed to process ticker data");
        setLoading(false);
      }
    } else if (ticker) {
      setLoading(true);
      const profileTemplate = currentProfile?.investment_profile_template || null;
      
      api.getTickerData(ticker, profileTemplate)
        .then((data: any) => {
          if (data && (Array.isArray(data) ? data.length > 0 : data)) {
            setTickerDetails(Array.isArray(data) ? data[0] : data);
          } else {
            setError("No data available for this ticker");
          }
          setLoading(false);
        })
        .catch((err: any) => {
          console.error("Error fetching ticker details:", err);
          setError("Failed to fetch ticker details");
          setLoading(false);
        });
    }
  }, [ticker, tickerInfo, api, currentProfile]);

  // --- Render Loading State ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} thickness={4} />
        <Box sx={{ ml: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Loading data for {ticker}...</Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a moment while we fetch the latest information.
          </Typography>
        </Box>
      </Box>
    );
  }

  // --- Render Error State ---
  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
         <Box sx={{ mb: 2 }}>
            <Button 
              variant="outlined" 
              onClick={onBack} 
              startIcon={<ArrowBackIcon />}
              sx={{
                borderRadius: '20px',
                fontWeight: 500
              }}
            >
              Back
            </Button>
         </Box>
        <Alert 
          severity="error" 
          variant="filled"
          sx={{ 
            py: 2, 
            borderRadius: 2,
            boxShadow: 2
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Error loading data for {ticker}
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // --- Render No Data State ---
  if (!tickerDetails) {
     return (
       <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
         <Box sx={{ mb: 2 }}>
            <Button 
              variant="outlined" 
              onClick={onBack} 
              startIcon={<ArrowBackIcon />}
              sx={{
                borderRadius: '20px',
                fontWeight: 500
              }}
            >
              Back
            </Button>
         </Box>
         <Alert 
           severity="warning"
           variant="filled"
           sx={{ 
             py: 2, 
             borderRadius: 2,
             boxShadow: 2
           }}
         >
           <Typography variant="subtitle1" fontWeight="bold">
             No data available for {ticker}
           </Typography>
         </Alert>
       </Box>
     );
  }

  // Calculate some additional values for display
  const priceChange = tickerDetails.close - (tickerDetails.prev_close || tickerDetails.close * (1 - tickerDetails.gain_last_1day / 100));
  const dailyPriceChange = formatCurrency(priceChange);
  const isPriceUp = tickerDetails.gain_last_1day > 0;
  
  // --- Render Main Content ---
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Back button */}
      <Box sx={{ mb: 3 }}>
        <Button 
          variant="outlined" 
          onClick={onBack} 
          startIcon={<ArrowBackIcon />}
          sx={{
            borderRadius: '20px',
            fontWeight: 500,
            px: 2,
            py: 1,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
            }
          }}
        >
          Back to Dashboard
        </Button>
      </Box>

      {/* Header Section with improved styling - using theme tokens */}
      <Card
        elevation={2}
        sx={{ 
          mb: 4,
          borderRadius: 3,
          backgroundColor: theme.palette.custom.bannerBg, // Use theme token instead of hardcoded color
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Ticker Symbol & Name */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography 
                    variant="h4" 
                    fontWeight="bold" 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: 'white' // Always white text regardless of theme
                    }}
                  >
                    {tickerDetails.ticker}
                    <Chip 
                      label={tickerDetails.sector_name} 
                      size="small" 
                      sx={{ 
                        ml: 1, 
                        fontWeight: 'medium',
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: 'white'
                      }}
                    />
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'white', opacity: 0.9 }}>
                    {tickerDetails.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                    {tickerDetails.industry}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            {/* Price Information */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ textAlign: {xs: 'left', sm: 'right'}, mt: {xs: 2, sm: 0} }}>
                <Typography 
                  variant="h3" 
                  fontWeight="bold"
                  sx={{ 
                    color: 'white',
                    mb: 0.5
                  }}
                >
                  ${tickerDetails.close?.toFixed(2)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: {xs: 'flex-start', sm: 'flex-end'} }}>
                  <Typography 
                    variant="h6"
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      color: isPriceUp ? '#AFFFB7' : '#FFABAB',
                      fontWeight: 600,
                      mr: 1
                    }}
                  >
                    {isPriceUp ? (
                      <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                    ) : (
                      <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
                    )}
                    {dailyPriceChange} ({formatPercent(tickerDetails.gain_last_1day / 100)})
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                    Today
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
        
        {/* Key Stats Cards */}
        <Box sx={{ px: 2, pb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard
                label="Market Cap"
                value={formatCurrency(tickerDetails.marketcap)}
                icon={<ShowChartIcon fontSize="small" />}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard
                label="Volume"
                value={tickerDetails.volume?.toLocaleString() || 'N/A'}
                icon={<TimelineIcon fontSize="small" />}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard
                label="P/E Ratio"
                value={tickerDetails.eps ? (tickerDetails.close / tickerDetails.eps).toFixed(2) : 'N/A'}
                icon={<AssessmentIcon fontSize="small" />}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard
                label="EPS"
                value={tickerDetails.eps?.toFixed(2) ?? 'N/A'}
                icon={<LocalAtmIcon fontSize="small" />}
                trend={tickerDetails.eps_yoy ? tickerDetails.eps_yoy / 100 : 0}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard
                label="Dividend Yield"
                value={formatPercent(tickerDetails.divident_to_close_ratio)}
                icon={<PriceCheckIcon fontSize="small" />}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <MetricCard
                label="Volatility"
                value={formatPercent(tickerDetails.volatility_last_1month / 100)}
                icon={<SpeedIcon fontSize="small" />}
                isAlert={tickerDetails.volatility_last_1month > 30}
              />
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* AI Model and Last Updated Info Bar */}
      <Box
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.background.paper, 0.7),
          border: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight={500} color="text.secondary" component="span">
            Last Updated:
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ ml: 1 }}>
            {tickerDetails.LastUpdated ? new Date(tickerDetails.LastUpdated).toLocaleString() : 'N/A'}
          </Typography>
        </Box>
      </Box>

      {/* Chart Section - Use tickerDetails state */}
      <Box sx={{ mb: 4 }}>
        {tickerDetails.historical_ratings ? (
          <>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 0.5,
                py: 0.5,
                opacity: 0.8
              }}
            >
              <InfoOutlinedIcon fontSize="small" color="action" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Chart shows historical data which may not reflect profile-specific adjustments
              </Typography>
            </Box>
            <StockChart data={tickerDetails.historical_ratings} height={500}/>
          </>
        ) : (
          <Alert severity="info">Historical rating data not available for chart.</Alert>
        )}
      </Box>

      {/* Analysis Sections - Use tickerDetails state */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.7 }}>
        <InfoOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          Strikethrough values show original data before profile adjustments
        </Typography>
      </Box>

      {/* Time Horizon Analysis - Redesigned with consistent color scheme */}
      <Paper 
        elevation={2}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 3,
          backgroundColor: 'background.paper'
        }}
      >
        <SectionHeader 
          title="Time Horizon Analysis" 
          icon={<ShowChartIcon />} 
          color={theme.palette.primary.main}
        />
        
        <Grid container spacing={2}>
          {[
            { title: 'Short Term (1 Month)', prefix: 'ShortTerm' },
            { title: 'Mid Term (3-6 Months)', prefix: 'SwingTrade' },
            { title: 'Long Term (1 Year)', prefix: 'LongTerm' }
          ].map((horizon) => {
            // Determine standardized rating for consistent color application
            const rating = tickerDetails[`${horizon.prefix}Rating`];
            const standardizedRating = mapRatingToSignal(rating);
            const ratingColor = getSignalColor(standardizedRating);
            
            // Determine if it's a buy/sell rating
            const isBuy = rating?.toLowerCase().includes('buy');
            const isSell = rating?.toLowerCase().includes('sell');
            
            const baseRating = tickerDetails.base ? tickerDetails.base[`${horizon.prefix}Rating`] : '';
            const baseStandardizedRating = mapRatingToSignal(baseRating);
            const baseRatingColor = getSignalColor(baseStandardizedRating);
            
            return (
              <Grid item xs={12} md={4} key={horizon.title}>
                <Card 
                  elevation={1} 
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    borderTop: 4,
                    borderColor: ratingColor
                  }}
                >
                  <CardHeader 
                    title={
                      <Typography variant="h6" fontWeight={600}>
                        {horizon.title}
                      </Typography>
                    }
                    action={
                      <Box sx={{ alignItems: 'center' }}>
                        <Box
                          sx={{
                            alignItems: 'center',
                            py: 0.5,
                            px: 1.5,
                            borderRadius: 4,
                            bgcolor: ratingColor,
                            color: 'common.white',
                            fontSize: '0.85rem',
                            fontWeight: 600
                          }}
                        >
                          {tickerDetails[`${horizon.prefix}Rating`] || 'N/A'}
                        </Box>
                        {baseRating !== '' && baseRating !== rating &&
                        <Box
                          sx={{
                            alignItems: 'center',
                            mt: 0.2,
                            py: 0.2,
                            px: 1.5,
                            borderRadius: 4,
                            bgcolor: baseRatingColor,
                            color: 'common.white',
                            fontWeight: 200,
                          }}
                        >
                          <Typography sx={{textDecoration: 'line-through', fontSize: '0.75rem', textAlign: 'center'}}>
                            {baseRating || 'N/A'}
                          </Typography>
                        </Box>
                      }
                      </Box>
                    }
                    sx={{ pb: 0 }}
                  />
                  
                  <CardContent>
                    <Grid container spacing={2}>
                      {/* Price Target */}
                      <Grid item xs={12}>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Price Target
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight="bold">
                              ${tickerDetails[`${horizon.prefix}PriceTarget`]?.toFixed(2) || 'N/A'}
                            </Typography>
                            
                            {tickerDetails.base?.[`${horizon.prefix}PriceTarget`] !== undefined && 
                             tickerDetails.base?.[`${horizon.prefix}PriceTarget`] !== tickerDetails[`${horizon.prefix}PriceTarget`] && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  textDecoration: 'line-through', 
                                  ml: 1,
                                  opacity: 0.8 
                                }}
                              >
                                ${tickerDetails.base?.[`${horizon.prefix}PriceTarget`].toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Price Range in a compact format */}
                          <Box 
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mt: 1,
                              fontSize: '0.875rem',
                              bgcolor: alpha(theme.palette.background.default, 0.6),
                              borderRadius: 1,
                              py: 0.5,
                              px: 1
                            }}
                          >
                            <Typography variant="body2" component="span">
                              Range: ${tickerDetails[`${horizon.prefix}PriceTargetLowerBound`]?.toFixed(2) || 'N/A'} - 
                              ${tickerDetails[`${horizon.prefix}PriceTargetUpperBound`]?.toFixed(2) || 'N/A'}
                            </Typography>
                            
                            {/* {(tickerDetails.base?.[`${horizon.prefix}PriceTargetLowerBound`] !== undefined &&
                              tickerDetails.base?.[`${horizon.prefix}PriceTargetUpperBound`] !== undefined) && 
                              (tickerDetails.base?.[`${horizon.prefix}PriceTargetLowerBound`] !== tickerDetails[`${horizon.prefix}PriceTargetLowerBound`] ||
                               tickerDetails.base?.[`${horizon.prefix}PriceTargetUpperBound`] !== tickerDetails[`${horizon.prefix}PriceTargetUpperBound`]) && (
                              <Typography 
                                variant="caption" 
                                color="text.secondary" 
                                sx={{ textDecoration: 'line-through', ml: 1, opacity: 0.8 }}
                              >
                                (${tickerDetails.base?.[`${horizon.prefix}PriceTargetLowerBound`].toFixed(2)} - 
                                ${tickerDetails.base?.[`${horizon.prefix}PriceTargetUpperBound`].toFixed(2)})
                              </Typography>
                            )} */}
                          </Box>
                        </Box>
                      </Grid>
                      
                      {/* Expected Return */}
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Projected Gain
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography 
                            variant="h6" 
                            fontWeight="bold"
                            color={(tickerDetails[`${horizon.prefix}Gain`] ?? 0) > 0 
                              ? 'success.main' 
                              : (tickerDetails[`${horizon.prefix}Gain`] ?? 0) < 0 
                                ? 'error.main' 
                                : 'text.primary'}
                            sx={{
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {(tickerDetails[`${horizon.prefix}Gain`] ?? 0) > 0 && (
                              <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} />
                            )}
                            {(tickerDetails[`${horizon.prefix}Gain`] ?? 0) < 0 && (
                              <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />
                            )}
                            {formatPercent((tickerDetails[`${horizon.prefix}Gain`] ?? 0)/100)}
                          </Typography>
                          
                          {tickerDetails.base?.[`${horizon.prefix}Gain`] !== undefined && 
                           tickerDetails.base?.[`${horizon.prefix}Gain`] !== tickerDetails[`${horizon.prefix}Gain`] && (
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                textDecoration: 'line-through', 
                                ml: 1,
                                opacity: 0.8 
                              }}
                            >
                              {formatPercent((tickerDetails.base?.[`${horizon.prefix}Gain`] ?? 0)/100)}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      
                      {/* Probability */}
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Probability
                        </Typography>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="h6" fontWeight="bold">
                            {formatPercent(tickerDetails[`${horizon.prefix}ProbabilityOfHittingTarget`] ? tickerDetails[`${horizon.prefix}ProbabilityOfHittingTarget`] / 100 : 0)}
                          </Typography>
                          
                          {/* {tickerDetails.base?.[`${horizon.prefix}ProbabilityOfHittingTarget`] !== undefined && 
                           tickerDetails.base?.[`${horizon.prefix}ProbabilityOfHittingTarget`] !== tickerDetails[`${horizon.prefix}ProbabilityOfHittingTarget`] && (
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                textDecoration: 'line-through', 
                                ml: 1,
                                opacity: 0.8 
                              }}
                            >
                              {formatPercent(tickerDetails.base?.[`${horizon.prefix}ProbabilityOfHittingTarget`] ? tickerDetails.base[`${horizon.prefix}ProbabilityOfHittingTarget`] / 100 : 0)}
                            </Typography>
                          )} */}
                        </Box>
                      </Grid>
                      
                      {/* Risk Metrics in a compact grid */}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Risk Metrics
                        </Typography>
                        
                        <Box 
                          sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: 1,
                            bgcolor: alpha(theme.palette.background.default, 0.6),
                            p: 1,
                            borderRadius: 1
                          }}
                        >
                          <Box>
                            <Typography variant="caption" display="block" fontWeight={500}>
                              Volatility
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight="medium">
                                {formatPercent(tickerDetails[`${horizon.prefix}_volatility`] ?? 0)}
                              </Typography>
                              
                              {/* {tickerDetails.base?.[`${horizon.prefix}_volatility`] !== undefined && 
                               tickerDetails.base?.[`${horizon.prefix}_volatility`] !== tickerDetails[`${horizon.prefix}_volatility`] && (
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ 
                                    textDecoration: 'line-through', 
                                    ml: 0.5,
                                    opacity: 0.8 
                                  }}
                                >
                                  {formatPercent(tickerDetails.base?.[`${horizon.prefix}_volatility`] ?? 0)}
                                </Typography>
                              )} */}
                            </Box>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" display="block" fontWeight={500}>
                              Sharpe Ratio
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" fontWeight="medium">
                                {tickerDetails[`${horizon.prefix}_sharpe_ratio`]?.toFixed(2) ?? 'N/A'}
                              </Typography>
                              
                              {tickerDetails.base?.[`${horizon.prefix}_sharpe_ratio`] !== undefined && 
                               tickerDetails.base?.[`${horizon.prefix}_sharpe_ratio`] !== tickerDetails[`${horizon.prefix}_sharpe_ratio`] && (
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ 
                                    textDecoration: 'line-through', 
                                    ml: 0.5,
                                    opacity: 0.8 
                                  }}
                                >
                                  {tickerDetails.base?.[`${horizon.prefix}_sharpe_ratio`]?.toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Analysis Factors
                        </Typography>
                        
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ color: 'success.main', mr: 0.5, mt: 0.4 }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </Box>
                            <Box component="span" sx={{ fontWeight: 500 }}>Tailwinds:</Box>
                            <Box component="span" sx={{ ml: 0.5 }}>{tickerDetails[`${horizon.prefix}Tailwinds`] ?? 'N/A'}</Box>
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Box component="span" sx={{ color: 'error.main', mr: 0.5, mt: 0.4 }}>
                            <ArrowDownward fontSize="small" />
                          </Box>
                          <Box component="span" sx={{ fontWeight: 500 }}>Headwinds:</Box>
                          <Box component="span" sx={{ ml: 0.5 }}>{tickerDetails[`${horizon.prefix}Headwinds`] ?? 'N/A'}</Box>
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Performance Metrics and Relative Strength - in a more compact layout */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Performance Metrics - Redesigned more compact */}
        <Grid item xs={12} md={6}>
          <Card 
            elevation={2} 
            sx={{ 
              height: '100%', 
              borderRadius: 3
            }}
          >
            <CardHeader
              title={
                <SectionHeader 
                  title="Performance Metrics" 
                  icon={<TrendingUpIcon />} 
                  color={theme.palette.primary.main} 
                />
              }
              sx={{ px: 3, pt: 2, pb: 0 }}
            />
            <CardContent sx={{ px: 2, pt: 1, pb: 2 }}>
              {/* Returns on one line */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, ml: 1 }}>Return Metrics</Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  { 
                    label: '2 Weeks', 
                    value: tickerDetails.gain_last_2weeks / 100,
                    icon: <TimelineIcon />,
                    colorKey: "success"
                  },
                  { 
                    label: '1 Month', 
                    value: tickerDetails.gain_last_1month / 100,
                    icon: <TimelineIcon />,
                    colorKey: "success"
                  },
                  { 
                    label: '6 Months', 
                    value: tickerDetails.gain_last_6months / 100,
                    icon: <TimelineIcon />,
                    colorKey: "success"
                  }
                ].map((metric) => {
                  const isPositive = metric.value > 0;
                  const isNegative = metric.value < 0;
                  
                  return (
                    <Grid item xs={4} key={metric.label}>
                      <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: isPositive 
                          ? alpha(theme.palette.success.main, 0.08) 
                          : isNegative 
                            ? alpha(theme.palette.error.main, 0.08)
                            : 'transparent',
                      }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            mr: 1,
                            bgcolor: isPositive 
                              ? theme.palette.success.main 
                              : isNegative 
                                ? theme.palette.error.main
                                : theme.palette.info.main
                          }}
                        >
                          {metric.icon}
                        </Avatar>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {metric.label}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            fontWeight="medium"
                            sx={{ 
                              color: isPositive 
                                ? theme.palette.success.main 
                                : isNegative 
                                  ? theme.palette.error.main
                                  : theme.palette.text.primary
                            }}
                          >
                            {formatPercent(metric.value)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
              
              {/* Risk metrics on one line */}
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, ml: 1 }}>Risk Metrics</Typography>
              <Grid container spacing={1}>
                {[
                  { 
                    label: 'Volatility', 
                    value: tickerDetails.volatility_last_1month / 100,
                    icon: <SpeedIcon />,
                    colorKey: "warning",
                    secondaryLabel: "1 Month"
                  },
                  { 
                    label: 'Sharpe Ratio', 
                    value: tickerDetails.sharpe_last_1month / 100,
                    icon: <AssessmentIcon />,
                    colorKey: "info",
                    secondaryLabel: "1 Month",
                    isSharp: true
                  }
                ].map((metric) => {
                  const displayValue = metric.isSharp 
                    ? (metric.value * 100).toFixed(2) 
                    : formatPercent(metric.value);
                  
                  return (
                    <Grid item xs={6} key={metric.label}>
                      <Box sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette[(metric.colorKey as 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary')].main, 0.08)
                      }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            mr: 1,
                            bgcolor: theme.palette[(metric.colorKey as 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary')].main
                          }}
                        >
                          {metric.icon}
                        </Avatar>

                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {metric.label}
                          </Typography>
                          <Typography 
                            variant="body1" 
                            fontWeight="medium"
                            sx={{ 
                              color: theme.palette[(metric.colorKey as 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary')].main
                            }}
                          >
                            {displayValue}
                            {metric.secondaryLabel && (
                              <Typography variant="caption" component="span" color="text.secondary" sx={{ ml: 0.5 }}>
                                {metric.secondaryLabel}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Relative Strength Analysis - More compact */}
        <Grid item xs={12} md={6}>
          <Card 
            elevation={2} 
            sx={{ 
              height: '100%', 
              borderRadius: 3
            }}
          >
            <CardHeader
              title={
                <SectionHeader 
                  title="Relative Strength Analysis (1 Month)" 
                  icon={<CompareArrowsIcon />}
                  color={theme.palette.primary.main}
                />
              }
              sx={{ px: 3, pt: 2, pb: 0 }}
            />
            <CardContent sx={{ px: 2, pt: 1, pb: 2 }}>
              {[
                {
                  label: 'vs S&P 500',
                  value: tickerDetails.relstrength_to_SPY_last_1month,
                  prediction: tickerDetails.ShortTermGain_adjusted_relative_to_SPY_ratio,
                  description: "Performance relative to the S&P 500 index"
                },
                {
                  label: 'vs Sector',
                  value: tickerDetails.relstrength_to_sector_last_1month,
                  prediction: tickerDetails.ShortTermGain_adjusted_relative_to_sector_ratio,
                  description: `Performance relative to the ${tickerDetails.sector_name} sector`
                }
              ].map((strength, index) => {
                const currentStrength = (strength.value ?? 1) - 1;
                const predictedStrength = (strength.prediction ?? 1) - 1;
                const isCurrentPositive = currentStrength > 0;
                const isPredictedPositive = predictedStrength > 0;
                
                return (
                  <Box 
                    key={strength.label} 
                    sx={{ 
                      mb: index === 0 ? 2 : 0,
                      p: 1,
                      borderRadius: 2,
                      border: 1,
                      borderColor: isCurrentPositive ? 'success.main' : 'error.main',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {strength.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {strength.description}
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Box 
                          sx={{ 
                            p: 0.75, 
                            borderRadius: 1,
                            border: 1,
                            borderColor: isCurrentPositive ? 'success.main' : 'error.main',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {isCurrentPositive ? (
                            <TrendingUpIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                          ) : (
                            <TrendingDownIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                          )}
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Current
                            </Typography>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium" 
                              color={isCurrentPositive ? 'success.main' : 'error.main'}
                            >
                              {formatPercent(currentStrength)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Box 
                          sx={{ 
                            p: 0.75, 
                            borderRadius: 1,
                            border: 1,
                            borderStyle: 'dashed',
                            borderColor: isPredictedPositive ? 'success.main' : 'error.main',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {isPredictedPositive ? (
                            <TrendingUpIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                          ) : (
                            <TrendingDownIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                          )}
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Predicted
                            </Typography>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              color={isPredictedPositive ? 'success.main' : 'error.main'}
                            >
                              {formatPercent(predictedStrength)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Accordion for Additional Details with Improved Styling */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Accordion 
          expanded={expandedAccordion} 
          onChange={() => setExpandedAccordion(!expandedAccordion)} 
          sx={{ 
            boxShadow: 3,
            borderRadius: '16px !important',
            '&::before': {
              display: 'none',
            },
            overflow: 'hidden'
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon sx={{ color: theme.palette.primary.contrastText }} />
            }
            aria-controls="additional-details-content"
            id="additional-details-header"
            sx={{ 
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, 
              color: theme.palette.primary.contrastText, 
              '&:hover': { 
                background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` 
              },
              py: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AssessmentIcon sx={{ mr: 1.5 }} />
              <Typography variant="subtitle1" fontWeight="medium">
                View More In-depth Details 
                <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.8 }}>
                  ({expandedAccordion ? "Hide" : "Show"})
                </Typography>
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, backgroundColor: alpha(theme.palette.background.paper, 0.4) }}>
            {tickerDetails && <AdditionalTickerDetails details={tickerDetails} />}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default TickerDetailView;